// Messagerie of the ACS prototype (#/messages): one thread per coach. Session-local DEMO: received messages are fixtures, sent
// messages live in memory for the page session (a reload returns to the fixtures). Nothing is sent, uploaded or delivered; no
// « distribué » / « lu » status is invented. A demo switch makes the next sends fail, to show « Réessayer ».
import { useSyncExternalStore } from "react";
import { TEAM } from "./studio-fixtures";
import type { ChatAttachment, ChatMessage } from "./adapted/chat-thread/chat-thread";

export const ME = "me";
export type CoachId = "alexandre" | "antoine";
export interface Coach { id: CoachId; name: string; role: string; photo: string }
/** Real names, roles and official portraits of the studio team (studio-fixtures, ACS homepage). Message contents are DEMO. */
export const COACHES: Coach[] = TEAM.slice(0, 2).map((t, i) => ({ id: (i === 0 ? "alexandre" : "antoine") as CoachId, name: t.firstName, role: t.role, photo: t.photo }));

export interface Context { label: string; href: string }
interface State { messages: Record<CoachId, ChatMessage[]>; unread: Record<CoachId, number>; context: Partial<Record<CoachId, Context>> }

let state: State = {
  messages: {
    alexandre: [
      { id: "f1", authorId: "alexandre", text: "Bravo pour ta régularité cette semaine. On garde les charges de la Séance A pour la prochaine fois.", createdAt: "2026-10-10T18:40:00", status: "sent" },
      { id: "f2", authorId: "alexandre", text: "Ton bilan mi-parcours est prévu jeudi 15 octobre à 18 h 30.", createdAt: "2026-10-12T09:12:00", status: "sent" },
    ],
    antoine: [],
  },
  unread: { alexandre: 1, antoine: 0 },
  context: {},
};
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach(l => l()); };
const patchMessage = (coach: CoachId, id: string, patch: Partial<ChatMessage>) =>
  set({ messages: { ...state.messages, [coach]: state.messages[coach].map(m => m.id === id ? { ...m, ...patch } : m) } });
let counter = 0;

export const messagerie = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  markRead: (coach: CoachId) => { if (state.unread[coach]) set({ unread: { ...state.unread, [coach]: 0 } }); },
  /** Opens a thread with a context card to send (from Séance or the bilan); the member can remove it before sending. */
  startWithContext: (coach: CoachId, context: Context) => set({ context: { ...state.context, [coach]: context } }),
  clearContext: (coach: CoachId) => set({ context: { ...state.context, [coach]: undefined } }),
  /** Local send: the message shows « Envoi… », then « Envoyé » or, with the demo switch, « Non envoyé » + « Réessayer ».
   *  Attachment URLs are object URLs of the picked files, kept for the session (never uploaded). */
  send: (coach: CoachId, text: string, files: File[]): Promise<void> => {
    const attachments: ChatAttachment[] = files.map((f, i) => ({ id: `a-${++counter}-${i}`, name: f.name, size: f.size,
      kind: f.type.startsWith("image/") ? "image" : f.type.startsWith("video/") ? "video" : "file", url: URL.createObjectURL(f) }));
    const id = `m-${Date.now()}-${++counter}`;
    const context = state.context[coach];
    set({ messages: { ...state.messages, [coach]: [...state.messages[coach], { id, authorId: ME, text: text || undefined, attachments, context, createdAt: new Date(), status: "sending" }] },
      context: { ...state.context, [coach]: undefined } });
    return settle(coach, id);
  },
  retry: (coach: CoachId, id: string) => { patchMessage(coach, id, { status: "sending" }); void settle(coach, id); },
};
function settle(coach: CoachId, id: string) {
  return new Promise<void>(resolve => window.setTimeout(() => { patchMessage(coach, id, { status: "sent" }); resolve(); }, 650));
}
export const useMessagerie = () => useSyncExternalStore(messagerie.subscribe, messagerie.get, messagerie.get);
export const lastAt = (m: ChatMessage[]) => m.length ? new Date(m[m.length - 1]!.createdAt).getTime() : -Infinity;
